import React from "react";
import { Paragraph } from "@arctheme-components/elements/Paragraph/Paragraph";
import { useFreeShipping } from "../../../../hooks/useFreeShipping";

export const CartTotals = ({ 
  cart_subtotal, 
  cart_items, 
  translations, 
  hasNoFreeShippingItems = false,
  isCheckingTags = false 
}) => {
  
  // Use the free shipping hook to check if threshold is met
  const { shippingData } = useFreeShipping(cart_subtotal);
  
  // Calculate total savings for items with a selling plan
  const totalSavings = cart_items.reduce((acc, item) => {
    if(item?.product_type == 'Bundle') {
      const discountedPrice = item?.price / 100;
      const discountPercentage = Number(item.properties._bundleDiscount)

      if (discountedPrice) {
        const originalPrice = discountedPrice / (1 - discountPercentage / 100);
        const amountSaved = originalPrice - discountedPrice;
        return acc + amountSaved;
      }
    }
    if (item.selling_plan_allocation) {
      const originalPrice = item.selling_plan_allocation.compare_at_price; // Original price before discount
      const discountedPrice = item.selling_plan_allocation.price; // Discounted price (current price)
      const itemSavings =
        ((originalPrice - discountedPrice) / 100) * item.quantity;
      return acc + itemSavings;
    } else if (item.line_level_discount_allocations.length > 0) {
      let itemDiscount = 0;
      item.line_level_discount_allocations.forEach(discount => {
        itemDiscount += discount.amount;
      });
      const itemSavings = ((itemDiscount) / 100);
      return acc + itemSavings;
    } else {
      const variant = item.product?.variants?.find(
        (v) => String(v.id) === String(item.variant_id)
      );
      const originalPrice = variant?.compare_at_price;
      if (originalPrice && originalPrice > item.price) {
        const discountedPrice = item.price;
        const itemSavings =
          ((originalPrice - discountedPrice) / 100) * item.quantity;
        return acc + itemSavings;
      }
    }
    return acc;
  }, 0);

  const currencySymbol = window.Alpine?.store('currency').getCurrencySymbol();

  // const totalBeforeSavings = totalSavings + cart_subtotal;
  const finalSubtotal = cart_subtotal;
  const originalSubtotal = finalSubtotal + totalSavings;

  // Determine shipping text based on free shipping threshold and excluded products
  const getShippingText = () => {
    if (isCheckingTags) {
      return "Checking eligibility...";
    }
    
    if (hasNoFreeShippingItems) {
      return translations?.calculated_at_checkout || "Calculated at checkout";
    }
    
    return shippingData?.isUnlocked 
      ? (translations?.free_shipping_calculated || "Free shipping calculated at checkout")
      : (translations?.calculated_at_checkout || "Calculated at checkout");
  };

  const shippingText = getShippingText();

  return (
    <div className={"py-[8px]"}>
      <div className="minicart__totals">
        <Paragraph text={translations?.subtotal || 'Subtotal'} type={"text-sm font-body text-primary font-semibold"} />
        <Paragraph text={`${currencySymbol}${originalSubtotal.toFixed(2)}`} type="font-body text-sm text-primary" />
      </div>
      {totalSavings > 0 && (
        <div className="minicart__totals">
          <Paragraph
            text={translations?.savings || 'Savings'}
            type={"text-sm font-body text-primary font-semibold"}
          />
          <Paragraph text={`-${currencySymbol}${totalSavings.toFixed(2)}`} type="font-body text-sm text-primary text-red-500" />
        </div>
      )}
      <div className="minicart__totals">
        <Paragraph text={translations?.shipping || 'Shipping'} type={"text-sm font-body text-primary font-semibold"} />
        <div className={"flex flex-col"}>
          <Paragraph
            text={shippingText}
            type={`text-sm font-body text-primary`}
          />
        </div>
      </div>
      <div className="minicart__totals">
        <Paragraph text={translations?.total || 'Total'} type={"text-sm font-body text-primary font-semibold"} />
        <Paragraph text={`${currencySymbol}${finalSubtotal.toFixed(2)}`} type="font-body text-sm text-primary" />
      </div>
    </div>
  );
};
